import React, { useMemo } from 'react';
import BubbleCommon from '@op/components/BubbleCommon';
import type { TipsMapType } from '@op/components/BubbleCommon';
import { getLanguage } from '@home/utils/common';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import type { IStudioTabInfoConfig } from './types';

function normalizeTipsLines(tips?: string | string[]): string[] {
  if (!tips) {
    return [];
  }
  if (Array.isArray(tips)) {
    return tips.map((line) => line.trim()).filter(Boolean);
  }
  return tips.split('\n').map((line) => line.trim()).filter(Boolean);
}

function renderTipsLines(lines: string[]): React.ReactNode {
  if (!lines.length) {
    return null;
  }
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {index > 0 && <br />}
      {line}
    </React.Fragment>
  ));
}

function buildTipsMap(
  tipsEn?: string | string[],
  tipsZh?: string | string[],
): TipsMapType | null {
  const enLines = normalizeTipsLines(tipsEn);
  const zhLines = normalizeTipsLines(tipsZh);
  if (!enLines.length && !zhLines.length) {
    return null;
  }
  return {
    en: renderTipsLines(enLines),
    zh: renderTipsLines(zhLines),
  };
}

function hasTipsContent(tips?: string | string[]): boolean {
  return normalizeTipsLines(tips).length > 0;
}

export function isStudioTabInfoVisible(info?: IStudioTabInfoConfig): boolean {
  if (!info?.show) {
    return false;
  }
  return !!(
    info.labelEn
    || info.labelCn
    || hasTipsContent(info.tipsEn)
    || hasTipsContent(info.tipsZh)
  );
}

interface IStudioTabHeaderInfoProps {
  info?: IStudioTabInfoConfig;
}

const StudioTabHeaderInfo: React.FC<IStudioTabHeaderInfoProps> = ({ info }) => {
  const { isEN } = useLocal();

  const tipsMap = useMemo(
    () => buildTipsMap(info?.tipsEn, info?.tipsZh),
    [info?.tipsEn, info?.tipsZh],
  );

  if (!isStudioTabInfoVisible(info)) {
    return null;
  }

  const label = getLanguage(isEN, info?.labelEn, info?.labelCn);

  return (
    <div className="dbh-studio-query-tabs-info">
      <BubbleCommon iconStyle={{ marginLeft: 5 }} tipsMap={tipsMap}>
        {label}
      </BubbleCommon>
    </div>
  );
};

export default StudioTabHeaderInfo;
